import { Decimal } from 'decimal.js';
import { isNil } from 'es-toolkit';
import { HUNDRED, ONE } from '../constants/constants';

/** Net line total from quantity, unit price and discount. The recycling fee is
 * informational — it is already included in the unit price, never added on top. */
export function computeLineTotalAmountNoVat(
  quantity: Decimal | null | undefined,
  unitPriceNoVat: Decimal | null | undefined,
  discountPercent: Decimal | null | undefined
): Decimal | null {
  if (isNil(quantity) || isNil(unitPriceNoVat)) return null;

  const discountMultiplier = isNil(discountPercent)
    ? ONE
    : ONE.minus(discountPercent.dividedBy(HUNDRED));

  return quantity.times(unitPriceNoVat).times(discountMultiplier);
}

/** Gross line total from the net total plus VAT (explicit amount or derived from rate). */
export function computeLineTotalAmountInclVat(
  totalAmountNoVat: Decimal | null | undefined,
  vatAmount: Decimal | null | undefined,
  vatRate: Decimal | null | undefined
): Decimal | null {
  if (isNil(totalAmountNoVat)) return null;

  if (!isNil(vatAmount)) {
    return totalAmountNoVat.plus(vatAmount);
  }

  const vatMultiplier = isNil(vatRate) ? ONE : ONE.plus(vatRate.dividedBy(HUNDRED));
  return totalAmountNoVat.times(vatMultiplier);
}

/** Reverse-derives the net unit price from a net line total. Null when not derivable. */
export function computeUnitPriceNoVatFromTotalNoVat(
  totalAmountNoVat: Decimal | null | undefined,
  quantity: Decimal | null | undefined,
  discountPercent: Decimal | null | undefined
): Decimal | null {
  if (isNil(totalAmountNoVat) || isNil(quantity) || quantity.isZero()) return null;

  const discountMultiplier = isNil(discountPercent)
    ? ONE
    : ONE.minus(discountPercent.dividedBy(HUNDRED));
  if (discountMultiplier.isZero()) return null;

  return totalAmountNoVat.dividedBy(quantity.times(discountMultiplier));
}
